import { READY_TICK_VERSION } from '../net/cooldown.js';
import { COMPACT_ENCODING, decodeWire, WIRE_MARK, type WireEncoding } from '../net/wire-compact.js';
import { PredictedClient } from '../net/client.js';
import { MONSTER_INTERPOLATION_DELAY_TICKS } from '../net/replication-core.js';
import type { BasicAttackCommand, InputAction, PresentationEvent } from '../net/protocol.js';
import type { ClientMessage, ServerMessage } from '../net/websocket-messages.js';
import type { ApiResult, MeView, WorldView } from '../game/http-types.js';
import { resolveViewportReport, type ViewportReport } from '../game/viewport-report.js';

export type ConnectionState = 'logged-out' | 'connecting' | 'connected' | 'retrying' | 'offline';
export const RETRY_DELAYS_MS = Object.freeze([250, 500, 1000, 2000, 4000] as const);
/** Valid WebSocket close code for a client-initiated reconnect; browsers reject codes outside 1000 and 3000-4999. */
const RECOVER_CLOSE_CODE = 4000;
/** The server closes a socket with this code when the same account connected elsewhere; reconnecting would make two tabs evict each other forever. */
const REPLACED_CLOSE_CODE = 4001;
/** Server errors sent after welcome that reject one message and leave the session usable. */
const TRANSIENT_SERVER_ERRORS: ReadonlySet<string> = new Set(['ACK rejected', 'rate limit exceeded', 'input rejected', 'viewport rejected', 'invalid message']);
export interface BrowserSocket {
  readonly readyState: number;
  addEventListener(type: 'open' | 'close' | 'error' | 'message', listener: (event: any) => void): void;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}
export interface BrowserResponse { readonly status: number; readonly ok: boolean; json(): Promise<unknown>; }
export interface BrowserConnectionDependencies {
  fetch(url: string, init?: { credentials?: 'same-origin' }): Promise<BrowserResponse>;
  socket(url: string): BrowserSocket;
  setTimeout(callback: () => void, delay: number): unknown;
  clearTimeout(handle: unknown): void;
  location: { origin: string; protocol: string };
}
export type BrowserConnectionEvent =
  | { type: 'state'; state: ConnectionState }
  | { type: 'view'; client: PredictedClient }
  | { type: 'presentation'; events: PresentationEvent[] }
  | { type: 'error'; message: string };
export interface BrowserInputCommand { moveX: number; moveY: number; aimAngle: number; actions: InputAction[]; basicAttackCommand: BasicAttackCommand; autoSkillSlots: number[]; }

function socketUrl(location: BrowserConnectionDependencies['location']): string { return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${new URL(location.origin).host}/game`; }
function closeReason(message: string): string {
  const encoder = new TextEncoder(); let reason = '';
  for (const character of message) { if (encoder.encode(reason + character).length > 123) break; reason += character; }
  return reason;
}
function decodeResult<T>(value: unknown): ApiResult<T> | undefined { return typeof value === 'object' && value !== null && 'ok' in value ? value as ApiResult<T> : undefined; }

export class BrowserGameConnection {
  state: ConnectionState = 'offline';
  predicted: PredictedClient | undefined;
  me: MeView | undefined;
  world: WorldView | undefined;
  private socket: BrowserSocket | undefined;
  private reconnectToken: string | undefined;
  private retryIndex = 0;
  private retryTimer: unknown;
  private stopped = false;
  private startGeneration = 0;
  private welcomed = false;
  private encoding: WireEncoding = 'plain';
  private latestServerTick = 0;
  private lastViewportSignature: string | undefined;
  private readonly listeners = new Set<(event: BrowserConnectionEvent) => void>();

  constructor(private readonly dependencies: BrowserConnectionDependencies) {}
  subscribe(listener: (event: BrowserConnectionEvent) => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  private emit(event: BrowserConnectionEvent): void { for (const listener of this.listeners) listener(event); }
  private transition(state: ConnectionState): void { this.state = state; this.emit({ type: 'state', state }); }

  async start(): Promise<void> {
    const generation = ++this.startGeneration; const stale = () => generation !== this.startGeneration;
    this.stopped = false; this.retryIndex = 0;
    const meResponse = await this.dependencies.fetch('/api/me', { credentials: 'same-origin' }); if (stale()) return;
    if (meResponse.status === 401) { this.transition('logged-out'); return; }
    if (!meResponse.ok) { this.fail('could not load account'); return; }
    const me = decodeResult<MeView>(await meResponse.json()); if (stale()) return; if (!me?.ok) { this.fail(me && !me.ok ? me.error.message : 'invalid account response'); return; }
    const worldResponse = await this.dependencies.fetch('/api/world', { credentials: 'same-origin' }); if (stale()) return;
    if (worldResponse.status === 401) { this.transition('logged-out'); return; }
    const world = worldResponse.ok ? decodeResult<WorldView>(await worldResponse.json()) : undefined; if (stale()) return;
    if (!world?.ok) { this.fail(world && !world.ok ? world.error.message : 'could not load world'); return; }
    this.me = me.value; this.world = world.value; this.open(false);
  }

  sendInput(input: BrowserInputCommand): boolean {
    if (this.state !== 'connected' || !this.predicted || !this.socket || this.socket.readyState !== 1) return false;
    const command = this.predicted.command(this.latestServerTick, input.moveX, input.moveY, input.aimAngle, input.actions, input.basicAttackCommand, input.autoSkillSlots);
    this.send({ type: 'input', command }); this.emit({ type: 'view', client: this.predicted }); return true;
  }

  /** Send display-only CSS dimensions and zoom, never camera/world coordinates. */
  reportViewport(report: ViewportReport): boolean {
    if (this.state !== 'connected' || !this.socket || this.socket.readyState !== 1) return false;
    try { resolveViewportReport(report); } catch { return false; }
    const signature = `${report.cssWidth}:${report.cssHeight}:${report.zoom}`;
    if (signature === this.lastViewportSignature) return true;
    this.send({ type: 'viewport', report });
    this.lastViewportSignature = signature;
    return true;
  }

  stop(): void { this.stopped = true; this.lastViewportSignature = undefined; if (this.retryTimer !== undefined) this.dependencies.clearTimeout(this.retryTimer); this.retryTimer = undefined; this.socket?.close(1000, 'client stopped'); this.transition('offline'); }

  private open(retry: boolean): void {
    if (this.stopped) return; this.lastViewportSignature = undefined; this.welcomed = false; this.encoding = 'plain';
    if (this.retryTimer !== undefined) { this.dependencies.clearTimeout(this.retryTimer); this.retryTimer = undefined; }
    if (this.socket && this.socket.readyState < 2) this.socket.close(1000, 'superseded');
    this.transition(retry ? 'retrying' : 'connecting');
    const socket = this.dependencies.socket(socketUrl(this.dependencies.location)); this.socket = socket; let recovered = false;
    socket.addEventListener('open', () => { if (socket !== this.socket) return; this.send({ type: 'connect', cooldownVersion: READY_TICK_VERSION, replicationEncoding: COMPACT_ENCODING, ...(this.reconnectToken ? { reconnectToken: this.reconnectToken } : {}) }); });
    socket.addEventListener('message', (event) => { if (socket !== this.socket || typeof event.data !== 'string') return; let message: ServerMessage; try { const parsed = JSON.parse(event.data); if (parsed[WIRE_MARK] !== undefined && this.encoding !== COMPACT_ENCODING) throw new Error('unnegotiated encoding'); message = decodeWire(parsed) as ServerMessage; } catch { this.recover('invalid server message'); return; } this.receive(message); });
    socket.addEventListener('error', () => { /* close owns bounded retry */ });
    socket.addEventListener('close', (event) => {
      if (socket !== this.socket || this.stopped || recovered) return; recovered = true;
      if (event?.code === REPLACED_CLOSE_CODE) { this.stopped = true; this.fail('다른 곳에서 접속됨'); return; }
      this.scheduleRetry();
    });
  }
  private receive(message: ServerMessage): void {
    if (message.type === 'error') {
      if (this.welcomed && TRANSIENT_SERVER_ERRORS.has(message.message)) this.emit({ type: 'error', message: message.message });
      else this.recover(message.message);
      return;
    }
    if (message.type === 'welcome') {
      if (message.replicationEncoding !== undefined && message.replicationEncoding !== 'plain' && message.replicationEncoding !== COMPACT_ENCODING) { this.recover('unsupported replication encoding'); return; }
      if (message.cooldownVersion !== undefined && message.cooldownVersion !== READY_TICK_VERSION) { this.recover('unsupported cooldown version'); return; }
      this.encoding = message.replicationEncoding ?? 'plain';
      // The server's tick can restart at 0 (deploy) or the room can be re-entered: replication always restarts from this welcome.
      this.latestServerTick = message.snapshot.serverTick; this.reconnectToken = message.reconnectToken; this.welcomed = true;
      this.predicted = new PredictedClient(message.playerNetId as import('../net/protocol.js').NetObjectId, message.tickRate, message.playerSpeedMps, 2, { terrain: this.world?.field.terrain }, MONSTER_INTERPOLATION_DELAY_TICKS);
      this.lastViewportSignature = undefined;
      this.predicted.receiveSnapshot(message.snapshot); this.retryIndex = 0; this.transition('connected'); this.emit({ type: 'view', client: this.predicted }); return;
    }
    if (!this.predicted || !this.predicted.receiveDelta(message.delta)) { this.recover('replication recovery required'); return; }
    this.latestServerTick = message.delta.serverTick; this.send({ type: 'ack', serverTick: message.delta.serverTick }); this.emit({ type: 'view', client: this.predicted });
    const events = this.predicted.drainPresentationEvents(); if (events.length) this.emit({ type: 'presentation', events });
  }
  private send(message: ClientMessage): void { if (this.socket?.readyState === 1) this.socket.send(JSON.stringify(message)); }
  /** The close reason carries why the client reconnects, so the server can log it; a browser rejects a reason over 123 UTF-8 bytes. */
  private recover(message: string): void { this.emit({ type: 'error', message }); const socket = this.socket; if (socket && socket.readyState < 2) socket.close(RECOVER_CLOSE_CODE, closeReason(message)); else this.scheduleRetry(); }
  private scheduleRetry(): void {
    if (this.stopped || this.retryTimer !== undefined) return;
    const delay = RETRY_DELAYS_MS[Math.min(this.retryIndex, RETRY_DELAYS_MS.length - 1)]!; this.retryIndex = Math.min(this.retryIndex + 1, RETRY_DELAYS_MS.length); this.transition('retrying'); this.retryTimer = this.dependencies.setTimeout(() => { this.retryTimer = undefined; this.open(true); }, delay);
  }
  private fail(message: string): void { this.emit({ type: 'error', message }); this.transition('offline'); }
}
